import React, { useState } from 'react';
import bowser from 'yola-bowser';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { i18next } from '@yola/ws-sdk';
import { designSystem } from '@yola/ws-ui';
import noop from 'yola-editor/src/js/utils/noop.js';
import stopPropagation from '../../../utils/stop-propagation';
import SearchHighlight from '../../../common/components/search-highlight.js';

const {
  Icon,
  Paragraph,
  DropdownContent,
  DropdownTrigger,
  Dropdown,
  ActionList,
  ActionListItem,
  ActionListDivider,
  IconButton,
  Checkbox,
  Tooltip,
} = designSystem;

const isTouchDevice = bowser.mobile || bowser.tablet;

function PageManagerTreeViewItem({
  id,
  name = '',
  title = '',
  password = undefined,
  iconGlyph,
  isActive = false,
  isFolder = false,
  isDraggable = true,
  isFolderEmpty = false,
  actions = [],
  isEmpty = false,
  isDragging = false,
  onAddPageToFolder = null,
  searchQuery = '',
  selectModeEnabled = false,
  selectedItemsMap = {},
  folderSelectionState = null,
  onItemSelect = Function.prototype,
  onClick,
}) {
  const [isAddFolderButtonVisible, setIsAddFolderButtonVisible] = useState(isTouchDevice);

  const className = classNames('ws-page-manager-tree-view-item', {
    'ws-page-manager-tree-view-item--active': isActive,
    'ws-page-manager-tree-view-item--empty': isEmpty,
    'ws-page-manager-tree-view-item--dragging': isDragging,
    'ws-page-manager-tree-view-item--select-mode': selectModeEnabled,
    'ws-page-manager-tree-view-item--with-gap': !selectModeEnabled,
    'ws-page-manager-tree-view-item--not-draggable': !isDraggable,
  });

  const handlers = {
    onMouseEnter: () => {
      setIsAddFolderButtonVisible(true);
    },
    onMouseLeave: () => {
      setIsAddFolderButtonVisible(false);
    },
  };

  const portalRootNode = document.querySelector('.ws-page-manager-modal');

  const handleSelectionChange = (event) => {
    event.stopPropagation();
    event.preventDefault();

    onItemSelect(id, event?.shiftKey);
  };

  const renderHighlightedName = (displayName) => (
    <span translate="no">
      <SearchHighlight text={displayName} searchQuery={searchQuery} />
    </span>
  );

  const getRegularItemContent = () => (
    <>
      <span className="ws-page-manager-tree-view-item__logo">
        <Icon glyph={iconGlyph} size={20} {...(isActive && { type: 'accent' })} />
        {password && (
          <span className="ws-page-manager-tree-view-item__key-logo">
            <Icon glyph="key" size={12} type="light" strokeWidth={1.5} />
          </span>
        )}
      </span>

      <span className="ws-page-manager-tree-view-item__title-wrapper">
        <Paragraph isClipped {...(isActive && { appearance: 'accent' })}>
          {renderHighlightedName(name || title)}
        </Paragraph>
      </span>
    </>
  );

  const handleIconClick = (event) => {
    if (!isFolder) {
      return;
    }

    event.stopPropagation();
    onClick();
  };

  const isChecked = isFolder
    ? (folderSelectionState?.checked ?? false)
    : Boolean(selectedItemsMap[id]);
  const isIndeterminate = isFolder ? (folderSelectionState?.indeterminate ?? false) : false;

  const getItemContent = () => {
    if (selectModeEnabled) {
      return (
        <>
          <span
            className="ws-page-manager-tree-view-item__checkbox"
            onClick={handleSelectionChange}
          >
            <Checkbox
              id={id}
              value={id}
              size="small"
              isLabelBold={false}
              checked={isChecked}
              indeterminate={isIndeterminate}
              onChange={noop}
            />
          </span>
          <span
            className="ws-page-manager-tree-view-item__logo"
            {...(isFolder && { onClick: handleIconClick })}
          >
            <Icon glyph={iconGlyph} size={20} {...(isActive && { type: 'accent' })} />
            {password && (
              <span className="ws-page-manager-tree-view-item__key-logo">
                <Icon glyph="key" size={12} type="light" strokeWidth={1.5} />
              </span>
            )}
          </span>
          <span className="ws-page-manager-tree-view-item__title-wrapper">
            <Paragraph isClipped {...(isActive && { appearance: 'accent' })}>
              {renderHighlightedName(name || title)}
            </Paragraph>
          </span>
        </>
      );
    }

    return getRegularItemContent();
  };

  return (
    <div
      className={className}
      data-item-id={id}
      onClick={selectModeEnabled ? handleSelectionChange : onClick}
      {...handlers}
    >
      {getItemContent()}

      {!selectModeEnabled && (actions.length > 0 || isFolder) && (
        <div className="ws-page-manager-tree-view-item__controls">
          {isAddFolderButtonVisible && isFolder && onAddPageToFolder && (
            <>
              <div
                className="ws-page-manager-tree-view-item__add-page-icon-wrapper"
                data-for="add-neew-page-tooltip"
                data-tip
              >
                <IconButton
                  transparent
                  glyph="plus-for-button"
                  size="small"
                  appearance="accent"
                  selected={isFolderEmpty}
                  onClick={onAddPageToFolder}
                />
              </div>

              <Tooltip
                id="add-neew-page-tooltip"
                placement="top"
                text={i18next.t('Add a new page')}
              />
            </>
          )}

          {actions.length > 0 && (
            <Dropdown
              placement="bottom-right"
              onClick={(e) => e.stopPropagation()}
              portalRootNode={portalRootNode}
              {...(isFolder && { width: 200 })}
            >
              <DropdownTrigger>
                <IconButton transparent glyph="more-2" size="medium" appearance="accent" />
              </DropdownTrigger>
              <DropdownContent>
                <ActionList withMobileBorder>
                  {actions.map((action) => (
                    <React.Fragment key={action.id}>
                      {action.divider === 'before' && <ActionListDivider />}
                      <ActionListItem
                        title={action.title}
                        iconGlyph={action.iconGlyph}
                        iconPlacement="left"
                        disabled={action.disabled}
                        // A disabled item has no action, but it must still swallow
                        // the click so it doesn't bubble up and toggle the folder.
                        onClick={action.disabled ? stopPropagation : action.onClick}
                        {...(action.iconType && { iconType: action.iconType })}
                        {...(action.component && { component: action.component })}
                        {...(action.tooltip && {
                          'data-for': `action-tooltip-${id}-${action.id}`,
                          'data-tip': true,
                        })}
                      />
                      {action.tooltip && (
                        <div className="ws-page-manager__tooltip-wrapper">
                          <Tooltip
                            key={action.id}
                            id={`action-tooltip-${id}-${action.id}`}
                            placement="top"
                            text={action.tooltip}
                          />
                        </div>
                      )}
                      {action.divider === 'after' && <ActionListDivider />}
                    </React.Fragment>
                  ))}
                </ActionList>
              </DropdownContent>
            </Dropdown>
          )}
        </div>
      )}
    </div>
  );
}

PageManagerTreeViewItem.propTypes = {
  id: PropTypes.string.isRequired,
  name: PropTypes.string,
  title: PropTypes.string,
  password: PropTypes.string,
  iconGlyph: PropTypes.string.isRequired,
  isActive: PropTypes.bool,
  isFolder: PropTypes.bool,
  isDraggable: PropTypes.bool,
  isFolderEmpty: PropTypes.bool,
  actions: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      iconGlyph: PropTypes.string.isRequired,
      iconType: PropTypes.string,
      divider: PropTypes.oneOf(['before', 'after']),
      component: PropTypes.func,
      title: PropTypes.string.isRequired,
      disabled: PropTypes.bool,
      tooltip: PropTypes.node,
      onClick: PropTypes.func.isRequired,
    })
  ),
  onClick: PropTypes.func.isRequired,
  isEmpty: PropTypes.bool,
  isDragging: PropTypes.bool,
  onAddPageToFolder: PropTypes.func,
  searchQuery: PropTypes.string,
  selectModeEnabled: PropTypes.bool,
  selectedItemsMap: PropTypes.shape({}),
  folderSelectionState: PropTypes.shape({
    checked: PropTypes.bool,
    indeterminate: PropTypes.bool,
  }),
  onItemSelect: PropTypes.func,
};

export default PageManagerTreeViewItem;
